<script lang="ts">
	import { beforeUpdate } from 'svelte';
	import { format } from 'svelte-i18n';
	import {
		DrawerTabs,
		DrawerTab,
		DrawerTabContent,
		IconButton,
		ParameterCard,
	} from '@Shelly/shelly-design-system';
	import { devices } from 'src/stores/lists/devices';
	import { temp_schemes } from 'src/stores/lists/scenes-derived';
	import ControlPreview from '../TemperatureScheme/TempSchemeDrawSections/ControlPreview.svelte';
	import ActuatorsPreview from '../TemperatureScheme/TempSchemeDrawSections/ActuatorsPreview.svelte';
	import SensorPreview from '../TemperatureScheme/TempSchemeDrawSections/SensorPreview.svelte';
	import InfoPreview from '../TemperatureScheme/TempSchemeDrawSections/InfoPreview.svelte';
	import ChartAndIntervalsPreview from '../TemperatureScheme/TempSchemeDrawSections/ChartAndIntervalsPreview.svelte';
	import ThermostatWizard from '../TemperatureScheme/TempSchemeWizard/ThermostatWizard.svelte';
	import { _deepClone } from '../../helpers/utilities';
	import type { ITempSchemeObj } from '../../types/TemperatureSchemeType';
	import { extractHexId } from '../TemperatureScheme/helpers/temp_scheme_to_ui';
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { status_configurator } from 'src/helpers/device_data_helpers';
	import {
		convertExtTempSensorValue,
		findDeviceSensorID,
		findExtTempSensor,
		handleThermostatTemps,
	} from '../TemperatureScheme/helpers/utils';
	import ThermoTempFavPositions from './ThermostatDrawer/ThermoTempFavPositions.svelte';
	import { sidePanelStateCombined } from 'src/stores/ui/sidepanel-public';
	import { real_id } from 'src/helpers/interface_helpers';
	import { isSubUnit } from 'src/helpers/user_helpers';

	let activeTabIndex = 0;
	let showAddEditModal = false;

	$: id = $sidePanelStateCombined?.id;
	$: thermostat = $temp_schemes[id] ? (_deepClone($temp_schemes[id]) as ITempSchemeObj) : undefined;

	$: name = thermostat?._meta?.name || '';
	$: type = thermostat?._meta?.type || '';

	// $: enabled = thermostat?._enabled;
	$: actuators =
		typeof thermostat === 'object'
			? thermostat?.toheat?.blk.reduce((acc, curr) => {
					let dev_id = extractHexId(curr?.r || '');
					if (dev_id) {
						return [
							...acc,
							$devices[dev_id] || {
								id: dev_id,
								deleted: true,
							},
						];
					}
					return acc;
				}, [])
			: [];

	$: sensorDecId = typeof thermostat === 'object' ? thermostat?.tschem?.r.split(':')[1] : undefined;

	$: deviceSensorId = findDeviceSensorID(sensorDecId, thermostat);

	$: deviceSensor = $devices[deviceSensorId] || {
		id: deviceSensorId,
		deleted: true,
	};

	$: statusStore = getDeviceStatus(deviceSensorId);
	$: deviceSensorIsOnline = $statusStore?.online;

	$: statusDeviceSensorStore = getDeviceStatus(deviceSensor ? deviceSensor.id : undefined);
	$: statusDeviceSensor = $statusDeviceSensorStore;
	$: settingsDeviceSensorStore = getDeviceSettings(deviceSensor ? deviceSensor.id : undefined);
	$: settingsDeviceSensor = $settingsDeviceSensorStore;

	$: extSensorsStatuses = status_configurator(statusDeviceSensor, deviceSensor, settingsDeviceSensor);

	$: units = typeof thermostat === 'object' ? (thermostat?.isinfh ? 'F' : 'C') : undefined;

	$: thermostatDefTemp = handleThermostatTemps(thermostat?.deftemp, units, thermostat?.isinfh);

	$: extTempSensor = findExtTempSensor(extSensorsStatuses, thermostat, deviceSensor);

	$: extTempSensorValue = convertExtTempSensorValue(
		extTempSensor?.value || extTempSensor?.sensor?.value,
		units,
	);

	const extract_icon_or_image = () => {
		if (thermostat?._meta?.image) {
			return { image: thermostat?._meta?.image };
		}

		return {
			icon:
				thermostat?._meta?.type === 'heat'
					? 'fal fa-temperature-sun'
					: 'fal fa-temperature-snow',
		};
	};

	$: drawerTabItems =
		typeof thermostat === 'object'
			? [
					{ ...extract_icon_or_image() },
					{
						icon: 'fal fa-chart-line',
					},
					{
						icon: 'fal fa-microchip',
					},
					{
						icon: 'fak fa-light-sensor-on-gear',
					},
					{
						icon: 'fal fa-info-circle',
					},
				]
			: [{ icon: 'fal fa-trash' }];

	const getSubHeading = () => {
		return typeof thermostat === 'object'
			? [
					$format('sub_settings_title_mode'), //type === 'heat' ? 'Mode: heating' : 'Mode: cooling',
					$format('sub_title_temperature_schedules', {
						default: 'Temperature schedules',
					}),
					$format('sub_title_selected_devices', {
						default: 'Selected devices',
					}),
					$format('label_info'),
				]
			: [];
	};

	// console.log('SUB HEADINGS ', SUB_HEADING);

	let SUB_HEADING = getSubHeading();

	beforeUpdate(() => {
		SUB_HEADING = getSubHeading();
	});

	const handlerThermostatEdit = () => {
		showAddEditModal = true;
		thermostat = _deepClone($temp_schemes[id]) as ITempSchemeObj;
	};
</script>

<DrawerTabs backgroundImgSrc="">
	<svelte:fragment slot="tabs">
		{#key activeTabIndex}
			{#each drawerTabItems as drawerTab, index}
				<DrawerTab
					icon={activeTabIndex === index ? `${drawerTab.icon} text-primary` : drawerTab.icon}
					image={drawerTab.image}
					active={activeTabIndex === index}
					on:click={() => {
						activeTabIndex = index;
					}}
				/>
			{/each}
		{/key}
	</svelte:fragment>
	{#key activeTabIndex}
		{#if typeof thermostat === 'object'}
			<DrawerTabContent
				class={activeTabIndex === 0 ? 'flex-direction-column flex-1' : ''}
				heading={name}
				subHeading={SUB_HEADING[activeTabIndex]}
			>
				<svelte:fragment slot="headingButton">
					{#if !isSubUnit()}
						<IconButton
							aria-label={$format('button_edit_thermostat', {
								default: 'Button edit thermostat',
							})}
							icon="fas fa-pen"
							on:click={handlerThermostatEdit}
						/>
					{/if}
				</svelte:fragment>

				{#if activeTabIndex === 0}
					<div class="flex-full-height" />
					<ControlPreview
						{id}
						{units}
						{thermostatDefTemp}
						{type}
						showType
						extSensorValue={extTempSensorValue}
						sensorIsOnline={Boolean(deviceSensorIsOnline)}
						extSensor={extTempSensor || {
							id: deviceSensorId,
							deleted: true,
							name: $devices[deviceSensorId]?.name || '',
						}}
					/>
				{:else if activeTabIndex === 1}
					<ChartAndIntervalsPreview {id} />
				{:else if activeTabIndex === 2}
					<SensorPreview
						device={$devices[deviceSensorId] || {
							id: deviceSensorId,
							deleted: true,
						}}
						extSensor={extTempSensor}
						extSensorValue={extTempSensorValue}
						sensorIsOnline={Boolean(deviceSensorIsOnline)}
					/>
					<ActuatorsPreview {actuators} />
				{:else if activeTabIndex === 3}
					<ThermoTempFavPositions {id} />
				{:else}
					<InfoPreview {id} value={extTempSensorValue} />
				{/if}
			</DrawerTabContent>
		{:else}
			<DrawerTabContent
				heading={$format('title_deleted_thermostat', {
					default: 'Deleted thermostat',
				})}
			>
				<div class="flex-full-height" />
				<ParameterCard
					icon="fas fa-temperature-half"
					title={$format('label_not_found_el', {
						default: 'Not found element',
					})}
					description={'ID:' + id}
					size="m"
				/>
			</DrawerTabContent>
		{/if}
	{/key}
</DrawerTabs>

{#if showAddEditModal}
	<ThermostatWizard {id} bind:showAddEditModal temp_scheme_object={thermostat} />
{/if}
